import {
  Badge,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useSelector } from "react-redux";
import KizWritingField from "./KizWritingField";
import KizSellStatusButton from "./KizSellStatusButton";
import KizCopyFields from "./KizCopyFields";
import KizReturnStatusChangeField from "./KizReturnStatusChangeField";
import WbStickerPrintButton from "./WbStickerPrintButton";
import { copyKizText } from "../../utils/kiz";

function StatusChip({ state }) {
  const map = {
    awaiting_packaging: { label: "Ожидает сборки", color: "warning" },
    ready_for_shipment: { label: "Готов к отгрузке", color: "info" },
    delivering: { label: "В доставке", color: "info" },
    delivered: { label: "Доставлено", color: "success" },
    cancelled: { label: "Отменено", color: "default" },
  };
  const s = map[state] || { label: state || "—", color: "default" };
  return <Chip size="small" color={s.color} label={s.label} />;
}

/**
 * Пропсы:
 * - posting: { posting_number, ... }
 * - items: [{ id, documentId, sku, offer_id, name, quantity, kiz_code, kiz_returned, withdrawal_status }]
 * - savingId: string|null — documentId позиции, которая сейчас сохраняется
 * - onChangeKiz(item, value)
 * - onSaveKiz(item)        — сохранить текущий kiz_code позиции
 * - onAddAdditionalKiz(item, code)
 * - onRemoveAdditionalKiz(item, code)
 * - onSetReturn(item, returned: boolean)
 * - onSetSold(item, status: 'on_balance'|'sold')
 * - onSendToWb(item)
 * - sendingWbId: string|null
 */
export default function PostingItemsCards({
  posting,
  items,
  savingId,
  onChangeKiz,
  onSaveKiz,
  onAddAdditionalKiz,
  onRemoveAdditionalKiz,
  savingAdditionalId,
  onSetReturn,
  onSetSold,
  onSendToWb,
  sendingWbId,
}) {
  const postingNum = posting?.posting_number || "";
  const status = useSelector((s) => s.ui.status);
  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <Typography variant="subtitle1">Постинг</Typography>
        {!!postingNum && (
          <Typography variant="body2" color="text.secondary">
            {postingNum}
          </Typography>
        )}
      </Stack>

      <Box sx={{ mb: 2 }}>
        <StatusChip state={posting?.state} />
      </Box>

      <Stack spacing={1.5}>
        {items.map((it) => {
          const addedCnt = it.kiz_code && String(it.kiz_code).trim() ? 1 : 0;
          const canPrintSticker =
            status === "awaiting_packaging" &&
            addedCnt > 0 &&
            posting?.market === "wb" &&
            posting?.wbOrderId;
          const extraCodes = Array.isArray(it.kiz_additional_codes)
            ? it.kiz_additional_codes
            : [];
          return (
            <Card key={it.id} variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent sx={{ "&:last-child": { pb: 2 } }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 0.5 }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {it.name}
                  </Typography>
                  <Badge
                    badgeContent={`${addedCnt}/${it.quantity || 1}`}
                    color={
                      addedCnt === (it.quantity || 1) ? "success" : "default"
                    }
                    sx={{ mr: 2 }}
                  />
                </Stack>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 1 }}
                >
                  SKU {it.sku} · Код товара {it.offer_id}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 1 }}
                >
                  Цена {it.price} ₽
                </Typography>

                {/*  Ввод КИЗ при "На сборке" или "готов к отгрузке"  */}
                {(status === "awaiting_packaging" ||
                  status === "ready_for_shipment") && (
                  <KizWritingField
                    it={it}
                    savingId={savingId}
                    onChangeKiz={onChangeKiz}
                    onSaveKiz={onSaveKiz}
                    onAddAdditionalKiz={onAddAdditionalKiz}
                    onRemoveAdditionalKiz={onRemoveAdditionalKiz}
                    savingAdditionalId={savingAdditionalId}
                    posting={posting}
                    onSendToWb={onSendToWb}
                    sendingWbId={sendingWbId}
                  />
                )}

                <KizCopyFields it={it} />

                {status === "cancelled" && (
                  <>
                    <KizReturnStatusChangeField
                      it={it}
                      onSetReturn={onSetReturn}
                    />
                  </>
                )}

                {status === "withdrawal" && (
                  <KizSellStatusButton it={it} onSetSold={onSetSold} />
                )}

                <Divider sx={{ my: 1.25 }} />
                {extraCodes.length > 0 &&
                  status !== "awaiting_packaging" &&
                  status !== "ready_for_shipment" && (
                    <>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mb: 0.5 }}
                      >
                        Дополнительные КИЗ:
                      </Typography>
                      <Stack spacing={0.5} sx={{ mb: 1 }}>
                        {extraCodes.map((code) => (
                          <Stack key={code} direction="row" spacing={1}>
                            <TextField
                              size="small"
                              fullWidth
                              value={code}
                              InputProps={{ readOnly: true }}
                            />
                            <Tooltip title="Скопировать дополнительный КИЗ">
                              <IconButton size="small" aria-label="Скопировать дополнительный КИЗ" onClick={() => copyKizText(code)}>
                                <ContentCopyIcon fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        ))}
                      </Stack>
                    </>
                  )}
                <Typography variant="caption" color="text.secondary">
                  {status !== "cancelled"
                    ? addedCnt > 0
                      ? "КИЗ добавлен"
                      : "КИЗ ещё не добавлены"
                    : it.kiz_returned
                      ? "КИЗ возвращён в оборот"
                      : "КИЗ выведен из оборота"}
                </Typography>
                {canPrintSticker && (
                  <WbStickerPrintButton wbOrderId={posting.wbOrderId} />
                )}
              </CardContent>
            </Card>
          );
        })}

        {items.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Позиции не найдены
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
